import React from 'react';
import { Badge, cn } from '@loader/ui';

/**
 * SHABadge
 * Reusable badge to display a short, monospace SHA/ID across pages.
 * Defaults to first 8 chars, with subtle background and tooltip for full ID.
 */
export interface SHABadgeProps {
  /** Full identifier (UUID or hash) */
  id: string;
  /** Number of leading characters to display (default: 8) */
  length?: number;
  /** Optional aria-label for accessibility */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
  /** When true, shows a tighter inline style (no background) */
  inline?: boolean;
}

export const SHABadge: React.FC<SHABadgeProps> = ({
  id,
  length = 8,
  ariaLabel = 'Identifier',
  className = '',
  inline = false,
}) => {
  if (!id) return null;

  const short = id.slice(0, length);

  if (inline) {
    return (
      <span
        className={cn('font-mono text-xs text-muted-foreground', className)}
        title={`Full ID: ${id}`}
        aria-label={ariaLabel}
      >
        {short}
      </span>
    );
  }

  return (
    <Badge
      variant="muted"
      className={cn('font-mono', className)}
      title={`Full ID: ${id}`}
      aria-label={ariaLabel}
    >
      {short}
    </Badge>
  );
};

export default SHABadge;
